import { MonoAmount } from '@/shared/ui/MonoAmount';
import { amountText, toNumber } from '@/shared/lib/money';
import { useT } from '@/shared/lib/i18n';
import type { LedgerOperation, OperationType } from './types';

function operationMeta(type: OperationType, amount: number) {
  switch (type) {
    case 'charge':
    case 'final_charge':
      return { tone: 'debt' as const, mark: 'K', sign: '+', ring: 'border-status-debt text-status-debt bg-[rgba(229,42,48,0.08)]' };
    case 'payment':
    case 'partial_payment':
      return { tone: 'paid' as const, mark: 'P', sign: '-', ring: 'border-transparent text-white bg-brand-gradient' };
    case 'credit_application':
    case 'charity_transfer':
      return { tone: 'credit' as const, mark: '+', sign: amount < 0 ? '-' : '+', ring: 'border-status-credit text-status-credit bg-[rgba(12,143,128,0.12)]' };
    case 'writeoff':
    case 'correction':
    case 'reversal':
      return { tone: 'muted' as const, mark: 'R', sign: amount < 0 ? '-' : '+', ring: 'border-text-muted text-text-muted bg-[rgba(18,52,36,0.06)]' };
    default:
      return { tone: 'neutral' as const, mark: '·', sign: '', ring: 'border-text-muted text-text-muted bg-white/40' };
  }
}

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export function OperationRow({ op, compact = false }: { op: LedgerOperation; compact?: boolean }) {
  const t = useT();
  const amount = toNumber(op.amount) ?? 0;
  const meta = operationMeta(op.type, amount);
  const value = `${meta.sign}${amountText(amount, op.currency)}`;

  return (
    <div className="flex items-center gap-3 py-3 border-b border-[rgba(18,52,36,0.08)] last:border-b-0">
      <div className={`w-[26px] h-[26px] rounded-full border-[1.5px] flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${meta.ring}`}>
        {meta.mark}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[14px] font-medium truncate">{t(`operation.types.${op.type}`)}</p>
        <p className="text-[12px] text-text-muted font-mono truncate">
          {formatDate(op.created_at)}
          {op.comment ? ` · ${op.comment}` : compact ? '' : ` · #${op.seq}`}
        </p>
      </div>
      <MonoAmount value={value} variant={meta.tone} size="sm" className="font-semibold" />
    </div>
  );
}
